import type { TimestampedAssemblyAITurnMessage } from "@/domains/dexie-db/audio-capture/types";
import type { ParsedTranscriptEvent, ParsedTranscriptWord } from "./desktop-event-parser";

export type DesktopTurnUpdate = {
  channel: 0 | 1;
  turn: TimestampedAssemblyAITurnMessage;
};

type ChannelState = {
  activeTurnOrder: number | null;
  nextTurnOrder: number;
};

const INITIAL_CHANNEL_STATE = (): ChannelState => ({
  activeTurnOrder: null,
  nextTurnOrder: 1,
});

export class DesktopTranscriptAssembler {
  private channelState: Record<0 | 1, ChannelState>;

  constructor() {
    this.channelState = {
      0: INITIAL_CHANNEL_STATE(),
      1: INITIAL_CHANNEL_STATE(),
    };
  }

  reset() {
    this.channelState[0] = INITIAL_CHANNEL_STATE();
    this.channelState[1] = INITIAL_CHANNEL_STATE();
  }

  ingest({
    channel,
    event,
  }: {
    channel: 0 | 1;
    event: ParsedTranscriptEvent;
  }): DesktopTurnUpdate | null {
    const state = this.channelState[channel];
    if (!state) return null;

    if (state.activeTurnOrder === null) {
      state.activeTurnOrder = state.nextTurnOrder;
    }

    const turnOrder = state.activeTurnOrder;
    const words = this.cloneWords(event.words);
    const turn: TimestampedAssemblyAITurnMessage = {
      type: "Turn",
      turn_order: turnOrder,
      turn_is_formatted: false,
      end_of_turn: event.endOfTurn,
      transcript: event.transcript,
      end_of_turn_confidence: 0,
      words,
      timestamp: event.timestamp,
    };

    if (event.endOfTurn) {
      state.activeTurnOrder = null;
      state.nextTurnOrder = turnOrder + 1;
    }

    return { channel, turn };
  }

  private cloneWords(words: ParsedTranscriptWord[]): TimestampedAssemblyAITurnMessage["words"] {
    return words.map(word => ({
      text: word.text,
      start: word.start,
      end: word.end,
      confidence: word.confidence,
    }));
  }
}
